import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { TouchableHighlight, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { addMonths, format, startOfDay } from 'date-fns';
import { AppView, CardView, Spacer } from 'design-system';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import createStyleSheets from 'utils/createStyleSheets';
import usePauseAndOptionallySkipAutoinvest from '../hooks/usePauseAndOptionallySkipAutoinvest';
import { PauseAutoinvestExpiryDateType } from '../types';
import { isWeb } from '../../../constants';
const Row = ({ title, subTitle, isChecked, onPress }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    return (<TouchableHighlight onPress={onPress} underlayColor={colors.background.underlay} activeOpacity={1}>
      <View style={styles.row}>
        <View style={styles.flex}>
          <Text Text-16>{title}</Text>
          <Text TextThin-14 Secondary>
            {subTitle}
          </Text>
        </View>
        <Spacer w={16}/>
        <RadioButton type="single" isChecked={isChecked}/>
      </View>
    </TouchableHighlight>);
};
const PauseAutoinvestInfoScreen = withAnimatedNavigationHeader(({ scrollHandler, route, navigation }) => {
    const { planId, skipUpcoming, expectedPaymentDate, activityId } = route.params;
    const { handleSave, fetching } = usePauseAndOptionallySkipAutoinvest({
        planId,
        skipUpcoming,
        expectedPaymentDate,
        activityId,
        popCount: 2,
    });
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const date = useMemo(() => startOfDay(new Date()), []);
    const [pauseSelection, setPauseSelection] = React.useState(undefined);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const data = useMemo(() => [
        {
            title: 'A month',
            finalDate: addMonths(date, 1),
            type: PauseAutoinvestExpiryDateType.MONTH,
            subTitle: `Until ${format(addMonths(date, 1), `EEEE d LLLL`)}`,
        },
        {
            title: 'Other',
            finalDate: undefined,
            subTitle: 'Choose the date',
            type: PauseAutoinvestExpiryDateType.CUSTOM,
        },
        {
            finalDate: date,
            title: 'Pause Autoinvest',
            type: PauseAutoinvestExpiryDateType.NO_LIMIT,
            subTitle: 'Collections will stop until you turn them back on',
        },
    ], [date]);
    const onSelectOption = useCallback((selection) => () => {
        setPauseSelection(selection);
    }, []);
    const onSave = useCallback(() => {
        if (pauseSelection) {
            if (pauseSelection.type === PauseAutoinvestExpiryDateType.CUSTOM) {
                navigation.navigate('PauseAutoinvestSelectDateScreen', {
                    planId,
                    skipUpcoming,
                    expectedPaymentDate,
                    activityId,
                });
                return;
            }
            handleSave(pauseSelection);
        }
    }, [activityId, expectedPaymentDate, handleSave, pauseSelection, planId, skipUpcoming]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="How long would you like to pause for?" subTitle="You'll get a notification when we resume investing"/>
          <CardView noMarginTop noMarginBottom>
            {data.map((eachData) => (<Row key={eachData.type} title={eachData.title} subTitle={eachData.subTitle} onPress={onSelectOption(eachData)} isChecked={eachData.type === pauseSelection?.type}/>))}
          </CardView>
        </Animated.ScrollView>
        <Button floating brand title="Save" isFetching={fetching} disabled={!pauseSelection} onPress={onSave}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
        paddingVertical: rem(20),
        paddingHorizontal: rem(16),
    },
    flex: {
        flex: 1,
    },
}));
export default PauseAutoinvestInfoScreen;
